/**
 * Hero Banner behavior:
 * - video autoplay (muted/inline) with play/pause control
 */
class HeroBanner extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {HTMLVideoElement[]} */
  #videos = [];
  /** @type {HTMLButtonElement | null} */
  #toggle = null;
  /** @type {MediaQueryList | null} */
  #mql = null;
  /** @type {HTMLVideoElement | null} */
  #activeVideo = null;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#videos = Array.from(this.querySelectorAll('video[data-hero-banner-video]'));
    this.#toggle = this.querySelector('[data-hero-banner-toggle]');
    this.#mql = window.matchMedia('(max-width: 749px)');

    this.#bind(signal);
    void this.#syncActiveVideo();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    this.#videos = [];
    this.#toggle = null;
    this.#mql = null;
    this.#activeVideo = null;
  }

  /** @param {AbortSignal} signal */
  #bind(signal) {
    if (this.#toggle) {
      this.#toggle.addEventListener('click', () => this.#toggleVideo(), { signal });
    }
    for (const video of this.#videos) {
      video.addEventListener('play', () => this.#onVideoStateChange(video), { signal });
      video.addEventListener('pause', () => this.#onVideoStateChange(video), { signal });
    }

    this.#mql?.addEventListener('change', () => {
      void this.#syncActiveVideo();
    }, { signal });
  }

  /** @param {HTMLVideoElement} video */
  #onVideoStateChange(video) {
    if (!this.#activeVideo || video !== this.#activeVideo) return;
    this.#syncToggle(!video.paused);
  }

  async #syncActiveVideo() {
    const next = this.#getActiveVideo();
    if (!next) return;

    if (this.#activeVideo && this.#activeVideo !== next) {
      this.#activeVideo.pause();
    }

    this.#activeVideo = next;
    await this.#initVideo(next);
  }

  /** @returns {HTMLVideoElement | null} */
  #getActiveVideo() {
    if (this.#videos.length === 0) return null;
    const hasMobileVideo = this.getAttribute('data-has-mobile-video') === 'true';
    const isMobile = Boolean(this.#mql?.matches);

    if (hasMobileVideo && isMobile) {
      return this.#videos.find((v) => v.dataset.heroBannerVideoViewport === 'mobile') ?? this.#videos[0];
    }

    return this.#videos.find((v) => v.dataset.heroBannerVideoViewport === 'desktop') ?? this.#videos[0];
  }

  /** @param {HTMLVideoElement} video */
  async #initVideo(video) {
    // Attempt autoplay on load; if blocked, show play icon.
    try {
      const p = video.play();
      if (p && typeof p.then === 'function') await p;
    } catch {
      if (!this.isConnected) return;
      this.#syncToggle(false);
      return;
    }

    if (!this.isConnected) return;
    this.#syncToggle(!video.paused);
  }

  #toggleVideo() {
    const video = this.#activeVideo;
    if (!video) return;
    if (video.paused) {
      void video.play();
    } else {
      video.pause();
    }
  }

  /** @param {boolean} isPlaying */
  #syncToggle(isPlaying) {
    if (!this.#toggle) return;
    const playIcon = this.#toggle.querySelector('.icon-play');
    const pauseIcon = this.#toggle.querySelector('.icon-pause');
    playIcon?.classList.toggle('hidden', isPlaying);
    pauseIcon?.classList.toggle('hidden', !isPlaying);
    this.#toggle.classList.remove('hidden');
  }
}

if (!customElements.get('hero-banner')) {
  customElements.define('hero-banner', HeroBanner);
}
